<template>
  <!-- The proposal card as it opens on a spot, without anything that needs an account behind it. -->
  <div ref="cardRef" class="spark-card">
    <span class="spark-spot">{{ localize('auth.landing.plan.spot') }}</span>
    <span class="spark-day">{{ localize('sparks.week.today') }}</span>

    <div class="spark-time">
      <GamButton
        :variant="GamButtonVariant.SECONDARY"
        :size="GamButtonSize.SMALL"
        :left-icon="GamIconName.SMALL_ARROW_LEFT"
        :is-disabled="startMinutes <= EARLIEST_START"
        is-icon-only
        @click="stepTime(-1)"
      />
      <span class="spark-digits">{{ timeLabel }}</span>
      <GamButton
        :variant="GamButtonVariant.SECONDARY"
        :size="GamButtonSize.SMALL"
        :left-icon="GamIconName.SMALL_ARROW_RIGHT"
        :is-disabled="startMinutes >= LATEST_START"
        is-icon-only
        @click="stepTime(1)"
      />
    </div>

    <div class="spark-bar">
      <!-- The spark card's status: who it is still waiting on, in its orange dot. -->
      <span class="spark-needed">
        <span class="spark-needed-dot" />
        {{ neededLabel }}
      </span>
      <div class="spark-bar-row">
        <GamChessSetCounter v-model="chessSets" :max="9" />
        <span class="spark-faces" aria-hidden="true">
          <span
            v-for="(src, index) in faces"
            :key="src"
            class="spark-face"
            :class="{ 'is-in': index < joined, 'is-unpaired': index === 1 }"
          >
            <span class="spark-face-photo">
              <img v-if="index < joined" :key="`${round}-${src}`" :src="src" alt="" />
            </span>
            <span v-if="index === 1" class="spark-unpaired">
              <span>{{ localize('attendee.badge.unpaired') }}</span>
            </span>
          </span>
        </span>
      </div>
    </div>

    <button type="button" class="spark-play" :aria-label="localize('auth.landing.start')" @click="emit('play')">
      <GamIcon :name="GamIconName.PLAY_EMPTY" :size="GamIconSize.X_LARGE" is-current />
    </button>
  </div>
</template>

<script setup lang="ts">
import { localize, localizeWithValues } from '@/core/gambits';
import GamButton from '@/views/components/GamButton.vue';
import GamChessSetCounter from '@/views/components/GamChessSetCounter.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const props = defineProps<{
  /** The players who join, in order. One place stays open. */
  faces: string[];
}>();

const emit = defineEmits<{ play: [] }>();

/** The card's own bounds: from eight in the morning to the last start at ten at night, in half hours. */
const STEP_MINUTES = 30;
const EARLIEST_START = 8 * 60;
const LATEST_START = 22 * 60;

const cardRef = ref<HTMLElement | null>(null);
const startMinutes = ref(18 * 60 + 30);
const chessSets = ref(1);
/** How many of the shown faces have arrived. One place stays open, so four needed becomes one. */
const joined = ref(0);
/** Bumps each lap so the arriving faces play their entrance again. */
const round = ref(0);
let joinTimer: ReturnType<typeof setTimeout> | undefined;
let visibility: IntersectionObserver | undefined;

const JOIN_MS = 900;
const LAP_HOLD_MS = 1400;

const neededLabel = computed(() => {
  const needed = Math.max(props.faces.length + 1 - joined.value, 1);
  if (needed === 1) return localize('auth.landing.plan.needed.one');
  return localizeWithValues('auth.landing.plan.needed.many', [String(needed)]);
});

function stopJoining() {
  if (joinTimer == null) return;
  clearTimeout(joinTimer);
  joinTimer = undefined;
}

/** Faces arrive one by one until one place is open, then the lap starts again. */
function startJoining() {
  stopJoining();
  round.value += 1;
  joined.value = 0;

  const step = () => {
    if (joined.value >= props.faces.length) {
      startJoining();
      return;
    }
    joined.value += 1;
    joinTimer = setTimeout(step, joined.value >= props.faces.length ? LAP_HOLD_MS : JOIN_MS);
  };

  joinTimer = setTimeout(step, JOIN_MS);
}

onMounted(() => {
  const card = cardRef.value;
  if (!card || typeof IntersectionObserver === 'undefined') {
    startJoining();
    return;
  }

  visibility = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      visibility?.disconnect();
      visibility = undefined;
      startJoining();
    },
    { threshold: 0.4 },
  );
  visibility.observe(card);
});

onBeforeUnmount(() => {
  visibility?.disconnect();
  stopJoining();
});

const timeLabel = computed(() => {
  const hours = Math.floor(startMinutes.value / 60);
  const minutes = startMinutes.value % 60;
  return `${hours}:${String(minutes).padStart(2, '0')}`;
});

function stepTime(direction: 1 | -1) {
  const next = startMinutes.value + direction * STEP_MINUTES;
  startMinutes.value = Math.min(LATEST_START, Math.max(EARLIEST_START, next));
}
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;

/* The map's proposal card: its surface, the spot over the time, and the bar under it. */
.spark-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  box-sizing: border-box;
  width: min(100%, 420px);
  padding: var(--spacing-xl);
  border: 1px solid var(--color-white-10);
  border-radius: var(--radius-large);
  background: var(--color-dark-700);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.55);
}

.spark-spot {
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.03em;
  color: var(--color-white-80);
}

.spark-day {
  height: 30px;
  padding: 0 0.75rem;
  border: 1px solid var(--color-pink);
  border-radius: 30px;
  font-size: 12px;
  font-weight: 500;
  line-height: 28px;
  letter-spacing: 0.03em;
  color: var(--color-pink);
}

.spark-time {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.spark-digits {
  width: 3em;
  font-size: 48px;
  font-weight: 200;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
  text-align: center;
  color: var(--color-white);
}

.spark-bar {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  margin-top: 0.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--color-white-20);
}

.spark-bar-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.spark-needed {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  height: 18px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  color: var(--color-orange);
}

.spark-needed-dot {
  width: 6px;
  height: 6px;
  min-width: 6px;
  border-radius: 50%;
  background-color: currentColor;
}

.spark-faces {
  display: flex;
  flex: none;
  align-items: center;
  height: 40px;
}

/* The sheet's faces, at the card's size. The circle clips the photo; the unpaired
   label sits on top of it, the same place it sits on an attendee. */
.spark-face {
  position: relative;
  display: flex;
  flex: none;
  width: 40px;
  height: 40px;

  &.is-in {
    visibility: visible;
    animation: spark-join 420ms ease;
  }

  &.is-unpaired.is-in {
    z-index: 1;
  }

  &:not(.is-in) {
    visibility: hidden;
  }

  & + & {
    margin-left: -8px;
  }
}

.spark-face-photo {
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 50%;
  background: var(--color-dark-500);
  box-shadow: 0 0 0 2px var(--color-dark-700);

  img {
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    padding: 14%;
    object-fit: contain;
  }
}

.spark-unpaired {
  position: absolute;
  top: 2px;
  left: 50%;
  z-index: 1;
  padding: 1px 3px;
  border-radius: 2rem;
  background: var(--color-linear-gradient-10), color-mix(in srgb, var(--color-white) 5%, var(--color-dark-700));
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  font-size: 6px;
  font-weight: 800;
  letter-spacing: 0.08em;
  white-space: nowrap;
  text-transform: uppercase;
  transform: translateX(-50%);

  span {
    background: var(--color-linear-gradient);
    background-clip: text;
    color: var(--color-pink);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
}

/* The card's Play circle, breathing as it does once there is someone to play. */
.spark-play {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.5rem;
  height: 3.5rem;
  margin-top: 0.75rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: var(--color-linear-gradient-30), rgba(0, 0, 0, 1);
  box-shadow: var(--shadow-button-action);
  color: var(--color-white);
  cursor: pointer;
  animation: spark-breathe 4s ease-in-out infinite;

  @include hover {
    background: var(--color-linear-gradient-30), rgba(255, 255, 255, 0.25);
  }
}

@keyframes spark-join {
  from {
    opacity: 0;
    transform: scale(0.6);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes spark-breathe {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.06);
  }
}
</style>
